import React, { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ImageIcon, Upload, X, Loader2 } from "lucide-react";

interface ImageUploadProps {
  onImageSelected: (base64: string, fileName: string) => void;
  isLoading: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ onImageSelected, isLoading }) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback((file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
      alert('Bitte nur Bilder hochladen (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Das Bild ist zu groß. Maximal 10 MB erlaubt.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setPreview(base64);
      setFileName(file.name);
      onImageSelected(base64, file.name);
    };
    reader.readAsDataURL(file);
  }, [onImageSelected]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }, [processFile]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const clearImage = () => {
    setPreview(null);
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          processFile(file);
          break;
        }
      }
    }
  }, [processFile]);

  if (preview) {
    return (
      <div className="relative rounded-xl border-2 border-slate-200 overflow-hidden bg-slate-50">
        <img 
          src={preview} 
          alt="Vorschau" 
          className="w-full max-h-[400px] object-contain"
        />
        <div className="absolute top-2 right-2 flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={clearImage}
            disabled={isLoading}
            className="bg-white/90 hover:bg-white shadow-lg"
          >
            <X className="h-4 w-4 mr-1" />
            Entfernen
          </Button>
        </div>
        <div className="p-3 bg-white border-t text-sm text-slate-600 flex items-center gap-2">
          <ImageIcon className="h-4 w-4" />
          {fileName}
        </div>
      </div>
    );
  }

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onPaste={handlePaste}
      tabIndex={0}
      className={`
        relative rounded-xl border-2 border-dashed transition-all cursor-pointer
        ${isDragOver 
          ? 'border-primary bg-primary/5 scale-[1.02]' 
          : 'border-slate-300 hover:border-primary/50 hover:bg-slate-50'
        }
      `}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={isLoading}
      />
      
      <div className="p-8 text-center">
        <div className={`
          mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors
          ${isDragOver ? 'bg-primary/10' : 'bg-slate-100'}
        `}>
          {isLoading ? (
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          ) : (
            <Upload className={`h-8 w-8 ${isDragOver ? 'text-primary' : 'text-slate-400'}`} />
          )}
        </div>
        
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          Bild hochladen
        </h3>
        <p className="text-slate-500 text-sm mb-4">
          Ziehen Sie ein Bild hierher, klicken Sie zum Auswählen, oder <strong>Strg+V</strong> zum Einfügen
        </p>
        <p className="text-xs text-slate-400">
          Screenshot, Foto von E-Mail oder SMS • JPG, PNG, WebP • Max. 10 MB
        </p>
      </div>
    </div>
  );
};
